import {DataObject} from "./DataObject";

export class EcommerceConnection implements DataObject {
  /**
   * Every boolean column on this row.
   *
   * The generic copy loops below are value-truthiness based, which is
   * fine for a string or a date and wrong for a flag: `if (obj[prop])`
   * skips `false`, so a connection the server says has
   * `synchronizeShipments: false` kept the client-side default of
   * `true`. Two consequences, both of them silent:
   *
   * - The settings UI showed the feature as ON for a store where it is
   *   OFF, and `toJSON()` emits every primitive -- so pressing Save on
   *   some unrelated field wrote `true` back and turned the feature on
   *   for real.
   * - It was not even self-consistent: `update()` (the refresh path)
   *   copies `false` correctly, so the same row read one way on first
   *   load and the other way after the next live update.
   *
   * MySQL `tinyint(1)` also reaches some callers as `0` / `1` rather
   * than a JSON boolean, which the truthiness loop leaves as a number
   * sitting in a boolean-typed field -- and `[value]="true"` on a radio
   * button does not match `1`. Coercing here keeps that out of the
   * templates.
   */
  private static readonly BOOLEAN_PROPERTIES: string[] = [
    'synchronizeProductsUsingStoredRemoteProductId',
    'send3DCover',
    'publishFeed',
    'sendEmailMessage',
    'synchronizeShipments',
    'useUpstreamPrice',
    'checkCostVsRevenue',
    'automaticallyReleaseOrders',
    'pollForRemoteOrders',
    'pollForRemoteProducts',
    'synchronizeProducts',
    'pushEcommerceProductImages',
    'pushEcommerceProductTitleAndDescription',
    'pushEcommerceProductMetaData',
    'pushEcommerceProductPrice',
    'pushEcommerceProductSKU'
  ];

  ID: number;
  org_ID: number;
  type: string | null;
  state: string;
  secret: string | null;
  remoteUser: string | null;
  remotePassword: string | null;
  remoteKey: string | null;
  defaultShippingService: string | null;
  defaultFreeShippingService: string | null;
  synchronizeProductsUsingStoredRemoteProductId: boolean;
  send3DCover: boolean;
  publishFeed: boolean;
  remoteURL: string | null;
  connectionStatus: string;
  orderCheckStatus: string;
  orderProcessedStatus: string;
  productReadStatus: string;
  productProcessedStatus: string;
  productImagesProcessedStatus: string;
  shipmentUpdateStatus: string;
  lastConnection: Date;
  lastOrderCheck: Date;
  lastProductRead: Date;
  lastOrderProcessed: Date;
  lastProductProcessed: Date;
  lastProductImagesProcessed: Date;
  lastShipmentProcessed: Date;
  connectonMessage: string;
  orderCheckMessage: string;
  orderProcessedMessage: string;
  productReadMessage: string;
  productProcessedMessage: string;
  shipmentUpdateMessage: string;
  /**
   * How an incoming order line is resolved to one of our `Product` records.
   *
   * Server enum (`docs/contracts/shared/EcommerceConnection.md`):
   *
   * | value                  | incoming line field | matched against                      |
   * | ---------------------- | ------------------- | ------------------------------------ |
   * | `ECOMMERCE_PRODUCT`    | `remoteProductId`   | `EcommerceProduct.remoteproduct_ID`  |
   * | `CUSTOMER_PRODUCT_ID`  | `sku`               | `Product.customerProduct_ID`         |
   * | `EAN13`                | `sku`               | `Product.EAN13`                       |
   * | `INTERNAL_PRODUCT_ID`  | `remoteProductId`   | `Product.ID`                          |
   *
   * `NULL`, `''` and any unrecognised value are treated as `ECOMMERCE_PRODUCT`
   * by the server, so the client default below must be `ECOMMERCE_PRODUCT` too
   * -- the constructor's copy loop only overwrites on a truthy value, so a row
   * that arrives NULL keeps whatever is defaulted here.
   */
  productIdentifierType: string;
  sendEmailMessage: boolean;
  notificationEmail: string | null;
  remoteOrderState: string;
  carrierServiceMap_ID: number;
  gatewayChargePercent: number;
  perOrderAmount: number;
  perBookAmount: number;
  currency: string;
  verification_ID: number;
  options: any;
  sendNotificationToEndConsumer: string;
  synchronizeShipments: boolean;
  useUpstreamPrice: boolean;
  checkCostVsRevenue: boolean;
  checkCostVsRevenueMargin: number;
  automaticallyReleaseOrders: boolean;
  automaticallyReleaseOrderHoldNumberOfHours: number;
  lastUpdate: Date;
  pollForRemoteOrders: boolean;
  pollForRemoteProducts: boolean;
  synchronizeProducts: boolean;
  pushEcommerceProductImages: boolean;
  pushEcommerceProductTitleAndDescription: boolean;
  pushEcommerceProductMetaData: boolean;
  pushEcommerceProductPrice: boolean;
  pushEcommerceProductSKU: boolean;
  /**
   * Provider-specific API version this connection is pinned to (e.g.
   * Shopify `2026-04`). `null` means "legacy / unpinned" — the server
   * falls back to the webhook default API version.
   *
   * Shared contract: `docs/contracts/shared/EcommerceConnection.md`.
   * Server populates `apiVersion = '2026-04'` for new Shopify App
   * OAuth installs; existing rows remain `null` until explicitly
   * upgraded.
   */
  apiVersion: string | null;

  /**
   * `undefined` means "the payload did not express a value", which is
   * NOT the same as `false` and must leave the existing value alone.
   */
  private static toBoolean(value: any): boolean | undefined {
    if ( typeof value === 'boolean' ) {
      return value;
    }
    if ( typeof value === 'number' ) {
      return value !== 0;
    }
    if ( typeof value === 'string' ) {
      const normalized = value.trim().toLowerCase();
      if ( normalized === 'true' || normalized === '1' ) {
        return true;
      }
      if ( normalized === 'false' || normalized === '0' ) {
        return false;
      }
    }
    return undefined;
  }

  /**
   * How a human tells THIS connection apart from the others.
   *
   * An organization can hold multiple EcommerceConnections, **including
   * multiple of the same type** -- several Shopify stores on one account is
   * normal rather than an edge case. Two consequences that the pickers in
   * this app got wrong until 2026-10-22:
   *
   * - `type` alone never identifies a connection. Three Shopify stores all
   *   render as "Shopify".
   * - Neither does the organization name. Every one of that organization's
   *   stores carries the same one, so it is pure repetition on an
   *   organization's own screen; it only earns its place in the `network` and
   *   `serviceProvider` roles, where the list spans organizations.
   *
   * `remoteURL` -- the store identifier -- is the only part the merchant
   * actually recognises, so it leads. The id is kept beside it because it is
   * what support asks for, and because `remoteURL` is user-editable and can
   * be blank on a half-configured row, in which case the id is all there is.
   */
  get storeLabel(): string {
    let store = (this.remoteURL || '').trim();
    if ( !store && this.options && this.options.shopifyStoreDomain ) {
      store = ('' + this.options.shopifyStoreDomain).trim();
    }
    return store ? store + ' (#' + this.ID + ')' : '#' + this.ID;
  }

  constructor( obj ) {
    // this.org_ID Make sure to set the orgId with newly created instances
    this.type = 'API';
    this.state = 'ACTIVE';
    this.secret = '';
    this.remoteUser = '';
    this.remotePassword = '';
    this.remoteKey = '';
    this.defaultShippingService = 'USPS_PRIORITY_FLAT';
    this.defaultFreeShippingService = 'USPS_PRIORITY_FLAT';
    this.synchronizeProductsUsingStoredRemoteProductId = true;
    this.send3DCover = false;
    this.publishFeed = false;
    this.remoteURL = '';
    this.connectionStatus = 'INITIAL';
    this.orderCheckStatus = 'INITIAL';
    this.orderProcessedStatus = 'INITIAL';
    this.productReadStatus = 'INITIAL';
    this.productProcessedStatus = 'INITIAL';
    this.productImagesProcessedStatus = 'INITIAL';
    this.shipmentUpdateStatus = 'INITIAL';
    this.lastConnection = new Date(0);
    this.lastOrderCheck = new Date(0);
    this.lastOrderProcessed = new Date(0);
    this.lastProductRead = new Date(0);
    this.lastProductProcessed = new Date(0);
    this.lastProductImagesProcessed = new Date(0);
    this.lastShipmentProcessed = new Date(0);
    this.connectonMessage = '';
    this.orderCheckMessage = '';
    this.orderProcessedMessage = '';
    this.productReadMessage = '';
    this.productProcessedMessage  = '';
    this.shipmentUpdateMessage = '';
    // Matches the server/DB default. Was 'CUSTOMER_PRODUCT_ID' until 2026-08-24,
    // which made the UI offer customerProduct_ID matching for every connection
    // whose column was NULL -- i.e. suggest products the pipeline would never
    // pick. See inbox/2026-08-24-product-identifier-type-ecommerce-product-default-correction.md
    this.productIdentifierType = 'ECOMMERCE_PRODUCT';
    this.sendEmailMessage = true;
    this.notificationEmail = '';
    this.remoteOrderState = '';
    this.carrierServiceMap_ID = 0;
    this.gatewayChargePercent = 0;
    this.perOrderAmount = 0;
    this.perBookAmount = 0;
    this.currency = 'USD';
    this.verification_ID = 0;
    this.options = {};
    this.sendNotificationToEndConsumer = 'SHIP';
    this.synchronizeShipments = true;
    this.useUpstreamPrice = true;
    this.checkCostVsRevenue = true;
    this.lastUpdate = new Date();
    this.pollForRemoteOrders = true;
    this.pollForRemoteProducts = true;
    this.synchronizeProducts = true;
    this.pushEcommerceProductImages = true;
    this.pushEcommerceProductTitleAndDescription = true;
    this.pushEcommerceProductMetaData = true;
    this.pushEcommerceProductPrice = true;
    this.pushEcommerceProductSKU = true;
    this.apiVersion = null;

    if ( obj ) {
      for ( const prop of Object.keys(obj) ) {
        if ( obj[prop] ) {
          this[prop] = obj[prop];
        }
      }
      if ( obj.lastConnection) {
        this.lastConnection = new Date(obj.lastConnection);
      }
      if ( obj.lastOrderCheck) {
        this.lastOrderCheck = new Date(obj.lastOrderCheck);
      }
      if ( obj.lastOrderProcessed) {
        this.lastOrderProcessed = new Date(obj.lastOrderProcessed);
      }
      if ( obj.lastProductRead) {
        this.lastProductRead = new Date(obj.lastProductRead);
      }
      if ( obj.lastProductProcessed) {
        this.lastProductProcessed = new Date(obj.lastProductProcessed);
      }
      if ( obj.lastProductImagesProcessed) {
        this.lastProductImagesProcessed = new Date(obj.lastProductImagesProcessed);
      }
      if ( obj.lastShipmentProcessed) {
        this.lastShipmentProcessed = new Date(obj.lastShipmentProcessed);
      }
      // `apiVersion` is nullable by contract — propagate explicit
      // `null` (the generic copy-loop above skips falsy primitives).
      if ( Object.prototype.hasOwnProperty.call(obj, 'apiVersion') ) {
        this.apiVersion = obj.apiVersion === undefined ? null : obj.apiVersion;
      }
      // Booleans last: the truthiness loop above cannot express `false`.
      this.copyBooleanProperties(obj);
    }
  }

  getId () {
    return this.ID;
  }

  getOrderValue () {
    return this.ID;
  }

  /**
   * Apply the boolean columns present on `obj`, preserving `false`.
   *
   * A property the payload does not carry at all is left alone, so a
   * partial update cannot reset a flag to its constructor default.
   */
  copyBooleanProperties(obj: any): void {
    if ( !obj ) {
      return;
    }
    for ( const prop of EcommerceConnection.BOOLEAN_PROPERTIES ) {
      if ( Object.prototype.hasOwnProperty.call(obj, prop) ) {
        const value = EcommerceConnection.toBoolean(obj[prop]);
        if ( value !== undefined ) {
          this[prop] = value;
        }
      }
    }
  }

  update (obj: any) {
    if (obj) {
      for (const prop of Object.keys(obj)) {
        if (obj[prop] !== null && (prop !== 'ID') && ( typeof obj[prop] === 'string' || typeof obj[prop] === 'number' || typeof obj[prop] === 'boolean' || obj[prop] instanceof String || obj[prop] instanceof Boolean || obj[prop] instanceof Number || obj[prop] instanceof Date )) {
          this[prop] = obj[prop];
        }
      }
      if ( obj.options) {
        this.options = obj.options;
      }
      if ( obj.lastConnection) {
        this.lastConnection = new Date(obj.lastConnection);
      }
      if ( obj.lastOrderCheck) {
        this.lastOrderCheck = new Date(obj.lastOrderCheck);
      }
      if ( obj.lastOrderProcessed) {
        this.lastOrderProcessed = new Date(obj.lastOrderProcessed);
      }
      if ( obj.lastProductRead) {
        this.lastProductRead = new Date(obj.lastProductRead);
      }
      if ( obj.lastProductProcessed) {
        this.lastProductProcessed = new Date(obj.lastProductProcessed);
      }
      if ( obj.lastProductImagesProcessed) {
        this.lastProductImagesProcessed = new Date(obj.lastProductImagesProcessed);
      }
      if ( obj.lastShipmentProcessed) {
        this.lastShipmentProcessed = new Date(obj.lastShipmentProcessed);
      }
      // `apiVersion` is nullable by contract — propagate an explicit
      // `null` so the client can reflect a server-side downgrade
      // back to "legacy/unpinned". The generic copy-loop above
      // skips `null` primitives by design.
      if ( Object.prototype.hasOwnProperty.call(obj, 'apiVersion') ) {
        this.apiVersion = obj.apiVersion === undefined ? null : obj.apiVersion;
      }
      // The loop above already keeps `false`, but it leaves a tinyint
      // `0` / `1` as a number. Normalize so the templates compare
      // against real booleans.
      this.copyBooleanProperties(obj);
    }
  }
  toJSON() {
    const obj = {};
    for (const prop of Object.keys(this)) {
      if (this[prop] !== null && ( typeof this[prop] === 'string' || typeof this[prop] === 'number' || typeof this[prop] === 'boolean' || this[prop] instanceof String || this[prop] instanceof Boolean || this[prop] instanceof Number || this[prop] instanceof Date )) {
        obj[prop] = this[prop];
      }
    }
    if ( this.options) {
      obj['options'] = this.options;
    }
    // `apiVersion` is contract-nullable; emit explicit `null` so the
    // server can distinguish "unpinned" from "field omitted".
    obj['apiVersion'] = this.apiVersion === undefined ? null : this.apiVersion;
    return obj;
  }


}

